import { useState, useEffect, useContext } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { rechargeWallet } from "../services/walletService";
import { getSiteSettings } from "../services/generalService";
import { getAllPlans } from "../services/planService";
import { AuthContext } from "../context/authContext";
import { message } from "antd";

function RechargeForm({
  selectedPlan,
  pricePerCredit,
  userId,
  updateCreditBalance,
  setIsProcessing,
  isProcessing,
  isPlan,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { setUserData, userData } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    const cardElement = elements.getElement(CardElement);
    const { token, error } = await stripe.createToken(cardElement);
    if (error) {
      message.error(error.message);
      setIsProcessing(false);
      return;
    }

    try {
      let payload;

      if (isPlan) {
        payload = {
          token: token.id,
          planId: selectedPlan,
          userId,
          isPlan: true,
        };
      } else {
        payload = {
          token: token.id,
          userId,
          credits: selectedPlan,
          pricePerCredit,
          isPlan: false,
        };
      }

      const response = await rechargeWallet(payload);
      updateCreditBalance(response.user.creditBalance);
      message.success(
        `${
          response.message ? response.message + " " : "Recharge successful!"
        }Credits have been added to your account.`
      );
      setUserData({ ...userData, subscribed: true });
      localStorage.setItem(
        "userData",
        JSON.stringify({ ...userData, subscribed: true })
      );
      localStorage.setItem("subscribed", "true");
      setTimeout(() => {
        navigate("/chat");
      }, 2000);
    } catch (err) {
      console.error(err);
      message.error("Failed to recharge wallet. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#1F2937",
                "::placeholder": { color: "#9CA3AF" },
              },
              invalid: {
                color: "#EF4444",
                iconColor: "#EF4444",
              },
            },
          }}
        />
      </div>
      <button
        type="submit"
        disabled={isProcessing}
        className={`w-full py-3 px-6 rounded-lg text-white font-semibold transition-all duration-300 text-lg ${
          isProcessing
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-[#e61f26] hover:bg-[#b91c1c] active:bg-[#991b1b]"
        }`}
      >
        {isProcessing
          ? "Processing..."
          : isPlan
          ? "Subscribe Now"
          : "Recharge Now"}
      </button>
    </form>
  );
}

function Recharge() {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem("userId");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [stripePublicKey, setStripePublicKey] = useState("");
  const [pricePerCredit, setPricePerCredit] = useState(0);
  const [activeTab, setActiveTab] = useState(
    location.state?.page_status || "freepay"
  );
  const predefinedPlans = [100, 200, 500, 1000];
  const { updateCreditBalance } = useContext(AuthContext);
  /*   const [subscriptionPrice, setSubscriptionPrice] = useState(0);
  const [subscriptionCredits, setSubscriptionCredits] = useState(0); */
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] =
    useState(null);

  const handlePlanSelect = (amount) => {
    setSelectedPlan(amount);
    setCustomAmount("");
    setErrorMessage("");
  };

  const handleCustomAmountChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value) || value <= 99) {
      setErrorMessage("Minimum limit is 100 credits.");
      setCustomAmount(e.target.value);
      setSelectedPlan(null);
    } else if (value > 10000) {
      setErrorMessage("Maximum limit is 10,000 credits.");
      setCustomAmount(value);
      setSelectedPlan(null);
    } else {
      setErrorMessage("");
      setCustomAmount(value);
      setSelectedPlan(value);
    }
  };

  const handleBack = () => {
    navigate("/chat");
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const result = await getSiteSettings();
        if (result?.data?.pricePerCredit) {
          setStripePublicKey(result.data.stripePublishableKey);
          /* setStripePublicKey(
            "pk_test_51QjljHGmfgzG6WnDiJIt8FInaUzALfdcczY9UjOseAZKpJyAdDGhukD5NWOzVyGSjnoOzt639H4MXg7vfcZkd8eW008v0LsroN"
          ); */
          setPricePerCredit(result.data.pricePerCredit);
          /*           setSubscriptionPrice(result.data.subscriptionPrice || 0);
          setSubscriptionCredits(result.data.subscriptionCredits || 0); */
        } else {
          console.error(
            "Stripe public key or price not found in site settings."
          );
        }
      } catch (error) {
        console.error("Failed to fetch site settings:", error);
      }
    };
    fetchSettings();

    const fetchSubscriptionPlans = async () => {
      try {
        const response = await getAllPlans(); // replace with your API
        if (response && Array.isArray(response)) {
          setSubscriptionPlans(response);
        } else {
          console.warn("No plans found in response:", response);
          setSubscriptionPlans([]);
        }
      } catch (err) {
        console.error("Failed to fetch subscription plans:", err);
      }
    };

    fetchSubscriptionPlans();
  }, []);

  const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <ToastContainer />
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg p-8">
        <button
          onClick={handleBack}
          className="flex items-center text-[#e61f26] hover:text-[#b91c1c] transition duration-300 mb-6"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Chat
        </button>

        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          {activeTab === "freepay" ? "Recharge Now" : "Subscribe Now"}
        </h1>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-8">
          <button
            className={`py-3 px-6 text-lg font-semibold transition-all duration-300 ${
              activeTab === "freepay"
                ? "border-b-2 border-[#e61f26] text-[#e61f26]"
                : "text-gray-500 hover:text-[#e61f26]"
            }`}
            onClick={() => setActiveTab("freepay")}
          >
            Chat Credits
          </button>
          <button
            className={`py-3 px-6 text-lg font-semibold transition-all duration-300 ${
              activeTab === "yearly"
                ? "border-b-2 border-[#e61f26] text-[#e61f26]"
                : "text-gray-500 hover:text-[#e61f26]"
            }`}
            onClick={() => setActiveTab("yearly")}
          >
            Subscription
          </button>
        </div>

        {activeTab === "freepay" && (
          <>
            <p className="text-gray-600 mb-6 text-lg">
              Select a plan or enter a custom amount to recharge your credits.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              {predefinedPlans.map((plan) => (
                <button
                  key={plan}
                  onClick={() => handlePlanSelect(plan)}
                  className={`py-4 px-6 rounded-lg text-center font-semibold text-lg transition-all duration-300 ${
                    selectedPlan === plan
                      ? "bg-[#e61f26] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-[#e61f26]/10 hover:text-[#e61f26]"
                  }`}
                >
                  {plan} Credits - $
                  {Math.round(plan * pricePerCredit * 100) / 100}
                </button>
              ))}
            </div>

            <div className="mb-6">
              <label
                htmlFor="customAmount"
                className="block text-lg font-medium text-gray-700 mb-2"
              >
                Custom Amount
              </label>
              <input
                id="customAmount"
                type="number"
                min="1"
                placeholder="Enter custom amount"
                value={customAmount}
                onChange={handleCustomAmountChange}
                className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#e61f26] transition-all duration-300"
              />
              {errorMessage && (
                <p className="text-red-500 text-sm mt-2">{errorMessage}</p>
              )}
            </div>

            {selectedPlan && (
              <div className="bg-[#e61f26]/5 border border-[#e61f26]/20 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-[#e61f26] mb-2">
                  Order Summary
                </h2>
                <p className="text-gray-600">
                  Credits: <span className="font-semibold">{selectedPlan}</span>
                </p>
                <p className="text-gray-600">
                  Total Amount:{" "}
                  <span className="font-semibold">
                    ${Math.round(selectedPlan * pricePerCredit * 100) / 100}
                  </span>
                </p>
              </div>
            )}

            {stripePromise && selectedPlan && (
              <Elements stripe={stripePromise}>
                <RechargeForm
                  selectedPlan={selectedPlan}
                  pricePerCredit={pricePerCredit}
                  userId={userId}
                  updateCreditBalance={updateCreditBalance}
                  setIsProcessing={setIsProcessing}
                  isProcessing={isProcessing}
                  isPlan={false}
                />
              </Elements>
            )}
          </>
        )}

        {activeTab === "yearly" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {subscriptionPlans.map((plan) => (
                <div
                  key={plan._id}
                  onClick={() => setSelectedSubscriptionPlan(plan)}
                  className={`cursor-pointer border rounded-lg p-6 transition-all duration-300 ${
                    selectedSubscriptionPlan?._id === plan._id
                      ? "bg-[#e61f26]/5 border-[#e61f26] shadow-lg"
                      : ""
                  }`}
                >
                  <h2 className="text-xl font-semibold text-[#e61f26] mb-2">
                    {plan.name || "Subscription Plan"}
                  </h2>
                  <p className="text-gray-600">
                    Credits:{" "}
                    <span className="font-semibold">{plan.credits}</span>
                  </p>
                  <p className="text-gray-600">
                    Total Amount:{" "}
                    <span className="font-semibold">${plan.price}</span>
                  </p>
                  <div className="mt-4 bg-white border border-[#e61f26]/30 rounded-md p-4">
                    <p className="text-gray-700">
                      By subscribing, you get to add
                      <span className="font-semibold text-[#e61f26]">
                        {" "}
                        unlimited Discord and Telegram channels
                      </span>
                      .
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {selectedSubscriptionPlan && stripePromise && (
              <div className="mt-6 w-full">
                <div className="bg-[#e61f26]/5 border border-[#e61f26]/20 rounded-lg p-6 mb-6">
                  <h2 className="text-xl font-semibold text-[#e61f26] mb-2">
                    Order Summary
                  </h2>
                  <p className="text-gray-600">
                    Credits:{" "}
                    <span className="font-semibold">
                      {selectedSubscriptionPlan.credits}
                    </span>
                  </p>
                  <p className="text-gray-600">
                    Total Amount:{" "}
                    <span className="font-semibold">
                      ${selectedSubscriptionPlan.price}
                    </span>
                  </p>
                </div>

                <Elements stripe={stripePromise}>
                  <RechargeForm
                    selectedPlan={selectedSubscriptionPlan._id}
                    pricePerCredit={null}
                    userId={userId}
                    updateCreditBalance={updateCreditBalance}
                    setIsProcessing={setIsProcessing}
                    isProcessing={isProcessing}
                    isPlan={true}
                  />
                </Elements>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Recharge;